import React, { useState, useEffect, useRef } from 'react';
import {
  Bot,
  Play,
  RotateCw,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  History,
  Shield,
  Layers,
  FileText,
  Filter,
  Flame,
  Info,
  Calendar,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
  getNewsAgentStatus,
  triggerNewsAgent,
  toggleNewsAgentSchedule,
} from '../../../services/newsNewsletterApi';
import AgentRunHistoryModal from './AgentRunHistoryModal';
import { usePermissions } from '../../../utils/usePermissions';

export default function NewsAgentControlPanel({ onProposalsUpdated }) {
  const { typeUser } = usePermissions();
  const isAdmin = typeUser === 'admin';

  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Form parameters for manual trigger
  const [mode, setMode] = useState('DRY_RUN');
  const [limit, setLimit] = useState(5);

  const pollingRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Polling automatique lorsque le statut est EN_COURS
  useEffect(() => {
    if (agentStatus?.status === 'EN_COURS') {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => {
          fetchStatus(true);
        }, 3000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [agentStatus?.status]);

  const fetchStatus = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const res = await getNewsAgentStatus();
      if (res?.success && res.data) {
        const prevStatus = agentStatus?.status;
        setAgentStatus(res.data);

        // Si une collecte vient de se terminer, recharger les propositions
        if (prevStatus === 'EN_COURS' && res.data.status !== 'EN_COURS') {
          if (onProposalsUpdated) onProposalsUpdated();
        }
      }
    } catch (err) {
      console.error('Erreur récupération statut News Agent:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const handleManualTrigger = async () => {
    if (!isAdmin) {
      Swal.fire({
        icon: 'error',
        title: 'Accès restreint',
        text: 'Seul un administrateur est habilité à déclencher une collecte manuelle.',
        confirmButtonColor: '#f59e0b',
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'Lancer une collecte manuelle ?',
      html: `
        <div style="text-align: left; font-size: 13px; color: #475569;">
          <p><strong>Mode sélectionné :</strong> ${mode}</p>
          <p><strong>Plafond :</strong> ${limit} articles</p>
          <p style="margin-top: 8px; color: #0284c7;">
            ${
              mode === 'INGEST'
                ? '⚠️ <strong>Attention :</strong> Le mode INGEST génère des propositions en attente de validation dans le back-office.'
                : 'ℹ️ <strong>Sécurité :</strong> Le mode DRY_RUN effectue le cycle complet (IA Gemini, déduplication) sans altérer la base de données.'
            }
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Démarrer la collecte',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#94a3b8',
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setTriggering(true);
      const isIngest = mode === 'INGEST';
      const res = await triggerNewsAgent({
        mode,
        limit: parseInt(limit, 10),
        confirmed: isIngest,
      });
      if (res?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Collecte initiée !',
          text: 'Le News Agent s’exécute en arrière-plan. Progression suivie en temps réel.',
          timer: 2000,
          showConfirmButton: false,
        });
        fetchStatus();
      }
    } catch (err) {
      Swal.fire({
        icon: 'warning',
        title: 'Opération refusée',
        text: err.message || 'Impossible de lancer la collecte.',
        confirmButtonColor: '#f59e0b',
      });
    } finally {
      setTriggering(false);
    }
  };

  const handleToggleSchedule = async () => {
    if (!isAdmin) return;
    const targetState = !agentStatus?.schedule_enabled;

    try {
      const res = await toggleNewsAgentSchedule(targetState);
      if (res?.success) {
        setAgentStatus((prev) => ({
          ...prev,
          schedule_enabled: targetState,
          next_scheduled_run: targetState
            ? 'Aujourd’hui à 08:00 ou 17:00 (Heure de Tunis)'
            : 'Planification suspendue par l’administrateur',
        }));
        Swal.fire({
          icon: 'info',
          title: targetState ? 'Planification activée' : 'Planification suspendue',
          text: targetState
            ? 'Les collectes automatiques de 08h00 et 17h00 sont actives.'
            : 'Les collectes automatiques sont temporairement suspendues.',
          timer: 1800,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Impossible de modifier la planification.',
      });
    }
  };

  const isRunning = agentStatus?.status === 'EN_COURS';
  const lastRun = agentStatus?.last_run;

  return (
    <div className="an-agent-panel">
      {/* Top Bar: Title, Status Badge, Global Controls */}
      <div className="an-agent-topbar">
        <div className="an-agent-identity">
          <div className={`an-agent-avatar ${isRunning ? 'running' : ''}`}>
            <Bot size={24} />
          </div>
          <div>
            <div className="an-agent-heading">
              <h2 className="an-agent-title">
                Agent IA — Veille Énergétique Tunisienne
              </h2>
              {/* Dynamic Status Badge */}
              <span
                className={`an-agent-badge ${
                  isRunning
                    ? 'running'
                    : agentStatus?.status === 'TERMINÉ'
                    ? 'success'
                    : agentStatus?.status === 'ÉCHEC'
                    ? 'error'
                    : 'idle'
                }`}
              >
                <span className="an-agent-dot" />
                {isRunning ? 'EN COURS DE COLLECTE' : agentStatus?.status || 'INITIALISATION'}
              </span>
            </div>
            <p className="an-agent-subtitle">
              Surveillance continue : STEG, ANME, Ministère de l'Énergie, JORT | Modèle Gemini 3.5 Flash
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="an-agent-actions">
          <button
            onClick={() => fetchStatus()}
            disabled={loading}
            className="an-agent-btn-icon"
            title="Rafraîchir l'état"
          >
            <RotateCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>

          <button
            onClick={() => setHistoryOpen(true)}
            className="an-agent-btn-outline"
          >
            <History size={15} style={{ color: '#D97706' }} /> Historique ({agentStatus?.last_run ? 'Durable' : 'Vide'})
          </button>

          {/* Schedule Toggle Button */}
          {isAdmin && (
            <button
              onClick={handleToggleSchedule}
              className={`an-agent-btn-schedule ${agentStatus?.schedule_enabled ? 'active' : 'suspended'}`}
              title="Activer ou désactiver les collectes automatiques à 08h00 et 17h00"
            >
              <Calendar size={15} />
              Auto 08h / 17h : {agentStatus?.schedule_enabled ? 'ACTIF' : 'SUSPENDU'}
            </button>
          )}
        </div>
      </div>

      {/* Main Grid: Details & Metrics */}
      <div className="an-agent-main-grid">
        {/* Left Col: Planning & Schedule Info */}
        <div className="an-agent-info-card">
          <div className="an-agent-info-row">
            <span className="an-agent-info-label">
              <Clock size={15} style={{ color: '#D97706' }} /> Prochaine collecte :
            </span>
            <span className="an-agent-info-val">{agentStatus?.next_scheduled_run || '08h00 / 17h00'}</span>
          </div>

          <div className="an-agent-info-row">
            <span className="an-agent-info-label">
              <Calendar size={15} /> Dernière exécution :
            </span>
            <span className="an-agent-info-val">
              {lastRun?.started_at || lastRun?.startedAt
                ? new Date(lastRun.started_at || lastRun.startedAt).toLocaleString('fr-FR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })
                : 'Aucune'}
            </span>
          </div>

          <div className="an-agent-info-row">
            <span className="an-agent-info-label">Déclencheur :</span>
            <span className="an-agent-pill-tag">
              {lastRun?.triggered_by || lastRun?.triggeredBy || 'AUTOMATIQUE'}
            </span>
          </div>

          <div className="an-agent-info-row">
            <span className="an-agent-info-label">Durée du cycle :</span>
            <span className="an-agent-info-val">
              {lastRun?.duration_seconds || lastRun?.durationSeconds || 0}s
            </span>
          </div>

          {/* Sources Badge List */}
          <div style={{ paddingTop: '4px' }}>
            <span className="an-agent-sources-label">
              Sources sous surveillance :
            </span>
            <div className="an-agent-sources-list">
              <span className="an-agent-source-chip">STEG</span>
              <span className="an-agent-source-chip">ANME</span>
              <span className="an-agent-source-chip">Ministère Énergie</span>
              <span className="an-agent-source-chip">JORT (Législation)</span>
            </div>
          </div>
        </div>

        {/* Middle Col: KPI Indicators */}
        <div className="an-agent-kpi-grid">
          <div className="an-agent-kpi-box amber">
            <span className="an-agent-kpi-title">Découverts</span>
            <p className="an-agent-kpi-val">
              {lastRun?.total_discovered || lastRun?.totalDiscovered || 0}
            </p>
            <span className="an-agent-kpi-sub">Pages scannées</span>
          </div>

          <div className="an-agent-kpi-box green">
            <span className="an-agent-kpi-title">Rédigés (IA)</span>
            <p className="an-agent-kpi-val">
              {lastRun?.total_drafted || lastRun?.totalDrafted || 0}
            </p>
            <span className="an-agent-kpi-sub">Pertinents solaire</span>
          </div>

          <div className="an-agent-kpi-box blue">
            <span className="an-agent-kpi-title">Propositions</span>
            <p className="an-agent-kpi-val">
              {lastRun?.total_ingested || lastRun?.totalIngested || 0}
            </p>
            <span className="an-agent-kpi-sub">En attente revue</span>
          </div>

          <div className="an-agent-kpi-box slate">
            <span className="an-agent-kpi-title">Doublons ignorés</span>
            <p className="an-agent-kpi-val">
              {lastRun?.total_duplicates || lastRun?.totalDuplicates || 0}
            </p>
            <span className="an-agent-kpi-sub">SHA-256 dédupliqués</span>
          </div>

          <div className="an-agent-kpi-box purple">
            <span className="an-agent-kpi-title">Appels d'offres</span>
            <p className="an-agent-kpi-val">
              {lastRun?.total_rejected || lastRun?.totalRejected || 0}
            </p>
            <span className="an-agent-kpi-sub">Exclus du flux news</span>
          </div>

          <div className="an-agent-kpi-box rose">
            <span className="an-agent-kpi-title">Erreurs</span>
            <p className="an-agent-kpi-val">
              {lastRun?.total_errors || lastRun?.totalErrors || (lastRun?.errors ? lastRun.errors.length : 0)}
            </p>
            <span className="an-agent-kpi-sub">Incidents logs</span>
          </div>
        </div>

        {/* Right Col: Admin Manual Launch Controls */}
        <div className="an-agent-trigger-card">
          <div>
            <span className="an-agent-trigger-title">
              <Flame size={15} /> Déclenchement Manuel
            </span>

            {/* Mode selection */}
            <div className="an-agent-form-group">
              <label className="an-agent-form-label">Mode d'exécution :</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={isRunning || !isAdmin}
                className="an-agent-form-select"
              >
                <option value="DRY_RUN">DRY_RUN (Simulation sans écriture)</option>
                {isAdmin && <option value="INGEST">INGEST (Import en PENDING_REVIEW)</option>}
                <option value="DISCOVERY">DISCOVERY (Inventaire rapide)</option>
              </select>
            </div>

            {/* Limit selection */}
            <div className="an-agent-form-group">
              <label className="an-agent-form-label">Plafond d'articles :</label>
              <select
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                disabled={isRunning || !isAdmin}
                className="an-agent-form-select"
              >
                <option value="3">3 articles</option>
                <option value="5">5 articles</option>
                <option value="10">10 articles</option>
                <option value="20">20 articles</option>
              </select>
            </div>
          </div>

          {/* Trigger Button */}
          {isAdmin ? (
            <button
              onClick={handleManualTrigger}
              disabled={isRunning || triggering}
              className="an-agent-btn-submit"
            >
              {isRunning ? (
                <>
                  <RotateCw size={16} className="animate-spin" /> Collecte en cours...
                </>
              ) : triggering ? (
                <>
                  <RotateCw size={16} className="animate-spin" /> Démarrage...
                </>
              ) : (
                <>
                  <Play size={16} style={{ fill: 'currentColor' }} /> Lancer une collecte maintenant
                </>
              )}
            </button>
          ) : (
            <div className="an-agent-notice-restricted">
              Déclenchement réservé aux administrateurs
            </div>
          )}
        </div>
      </div>

      {/* History Modal */}
      <AgentRunHistoryModal isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}
